import { api } from './axios';

const validateId = (id, name = 'ID') => {
    if (!id && id !== 0) throw new Error(`${name} is required`);
};

export const rulesApi = {
    /**
     * Получает список правил
     * @param {Object} params - Параметры фильтрации (battleId - optional)
     * @returns {Promise<Object>}
     */
    list: async (params = {}) => {
        try {
            const { data } = await api.get('/rules', { params });
            return data;
        } catch (error) {
            console.error('[rulesApi.list] Failed to fetch rules:', error.message);
            throw error;
        }
    },

    /**
     * Создаёт правило
     * @param {Object} payload - Данные правила (title, content, battleId, order)
     * @returns {Promise<Object>}
     */
    create: async (payload) => {
        if (!payload) throw new Error('Rule payload is required');
        try {
            const { data } = await api.post('/rules', payload);
            return data;
        } catch (error) {
            console.error('[rulesApi.create] Failed to create rule:', error.message);
            throw error;
        }
    },

    /**
     * Получает правило по ID
     * @param {string|number} id - ID правила
     * @returns {Promise<Object>}
     */
    get: async (id) => {
        validateId(id, 'Rule ID');
        try {
            const { data } = await api.get(`/rules/${id}`);
            return data;
        } catch (error) {
            console.error(`[rulesApi.get] Failed to fetch rule ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Обновляет правило
     * @param {string|number} id - ID правила
     * @param {Object} payload - Данные правила
     * @returns {Promise<Object>}
     */
    update: async (id, payload) => {
        validateId(id, 'Rule ID');
        if (!payload) throw new Error('Update payload is required');
        try {
            const { data } = await api.put(`/rules/${id}`, payload);
            return data;
        } catch (error) {
            console.error(`[rulesApi.update] Failed to update rule ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Удаляет правило
     * @param {string|number} id - ID правила
     * @returns {Promise<Object>}
     */
    remove: async (id) => {
        validateId(id, 'Rule ID');
        try {
            const { data } = await api.delete(`/rules/${id}`);
            return data;
        } catch (error) {
            console.error(`[rulesApi.remove] Failed to delete rule ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Получает статус правила
     * @param {string|number} id - ID правила
     * @returns {Promise<Object>}
     */
    getStatus: async (id) => {
        validateId(id, 'Rule ID');
        try {
            const { data } = await api.get(`/rules/${id}/status`);
            return data;
        } catch (error) {
            console.error(`[rulesApi.getStatus] Failed to fetch status for rule ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Обновляет статус правила
     * @param {string|number} id - ID правила
     * @param {string} status - ACTIVE | INACTIVE
     * @returns {Promise<Object>}
     */
    updateStatus: async (id, status) => {
        validateId(id, 'Rule ID');
        if (!status) throw new Error('Status is required');
        try {
            const { data } = await api.put(`/rules/${id}/status`, { status });
            return data;
        } catch (error) {
            console.error(`[rulesApi.updateStatus] Failed to update status for rule ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Получает список регламентов
     * @returns {Promise<Object>}
     */
    listRegulations: async () => {
        try {
            const { data } = await api.get('/regulations');
            return data;
        } catch (error) {
            console.error('[rulesApi.listRegulations] Failed to fetch regulations:', error.message);
            throw error;
        }
    },

    /**
     * Получает регламент по ID
     * @param {string|number} id - ID регламента
     * @returns {Promise<Object>}
     */
    getRegulation: async (id) => {
        validateId(id, 'Regulation ID');
        try {
            const { data } = await api.get(`/regulations/${id}`);
            return data;
        } catch (error) {
            console.error(`[rulesApi.getRegulation] Failed to fetch regulation ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Создаёт регламент
     * @param {Object} payload - Данные регламента
     * @returns {Promise<Object>}
     */
    createRegulation: async (payload) => {
        if (!payload) throw new Error('Regulation payload is required');
        try {
            const { data } = await api.post('/regulations', payload);
            return data;
        } catch (error) {
            console.error('[rulesApi.createRegulation] Failed to create regulation:', error.message);
            throw error;
        }
    },

    /**
     * Обновляет регламент
     * @param {string|number} id - ID регламента
     * @param {Object} payload - Данные регламента
     * @returns {Promise<Object>}
     */
    updateRegulation: async (id, payload) => {
        validateId(id, 'Regulation ID');
        if (!payload) throw new Error('Update payload is required');
        try {
            const { data } = await api.put(`/regulations/${id}`, payload);
            return data;
        } catch (error) {
            console.error(`[rulesApi.updateRegulation] Failed to update regulation ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Удаляет регламент
     * @param {string|number} id - ID регламента
     * @returns {Promise<Object>}
     */
    removeRegulation: async (id) => {
        validateId(id, 'Regulation ID');
        try {
            const { data } = await api.delete(`/regulations/${id}`);
            return data;
        } catch (error) {
            console.error(`[rulesApi.removeRegulation] Failed to delete regulation ${id}:`, error.message);
            throw error;
        }
    },

    // 🔴 Этап 8: Limitations
    /**
     * Получает список ограничений
     * @param {Object} params - Параметры фильтрации (regulationId - optional)
     * @returns {Promise<Object>}
     */
    listLimitations: async (params = {}) => {
        try {
            const { data } = await api.get('/limitations', { params });
            return data;
        } catch (error) {
            console.error('[rulesApi.listLimitations] Failed to fetch limitations:', error.message);
            throw error;
        }
    },

    /**
     * Получает ограничение по ID
     * @param {string|number} id - ID ограничения
     * @returns {Promise<Object>}
     */
    getLimitation: async (id) => {
        validateId(id, 'Limitation ID');
        try {
            const { data } = await api.get(`/limitations/${id}`);
            return data;
        } catch (error) {
            console.error(`[rulesApi.getLimitation] Failed to fetch limitation ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Создаёт ограничение
     * @param {Object} payload - Данные ограничения (type: TIME_LIMIT|WORD_LIMIT|AGE_LIMIT|OTHER, value, regulationId)
     * @returns {Promise<Object>}
     */
    createLimitation: async (payload) => {
        if (!payload) throw new Error('Limitation payload is required');
        try {
            const { data } = await api.post('/limitations', payload);
            return data;
        } catch (error) {
            console.error('[rulesApi.createLimitation] Failed to create limitation:', error.message);
            throw error;
        }
    },

    /**
     * Обновляет ограничение
     * @param {string|number} id - ID ограничения
     * @param {Object} payload - Данные ограничения
     * @returns {Promise<Object>}
     */
    updateLimitation: async (id, payload) => {
        validateId(id, 'Limitation ID');
        if (!payload) throw new Error('Update payload is required');
        try {
            const { data } = await api.put(`/limitations/${id}`, payload);
            return data;
        } catch (error) {
            console.error(`[rulesApi.updateLimitation] Failed to update limitation ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Удаляет ограничение
     * @param {string|number} id - ID ограничения
     * @returns {Promise<Object>}
     */
    removeLimitation: async (id) => {
        validateId(id, 'Limitation ID');
        try {
            const { data } = await api.delete(`/limitations/${id}`);
            return data;
        } catch (error) {
            console.error(`[rulesApi.removeLimitation] Failed to delete limitation ${id}:`, error.message);
            throw error;
        }
    }
};
